import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

// The art is a PNG rather than target.svg: that file wraps the same PNG in an SVG pattern with a
// fixed id, and on web a hidden copy of the pill (Home stays mounted under the screen on top)
// carries the same id, so `url(#id)` resolved to the hidden one and the icon painted nothing.
import DailyMissionsTargetPng from '../../../assets/header/target-pill.png';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { missionsModalOpened } from '../../../store/missions/missionsActions';
import {
	selectMissionsCompletedCount,
	selectMissionsTotalCount,
} from '../../../store/missions/missionsReducer';
import { useDevice } from '../../../utils/hooks/useDevice';
import { Pressable } from '../../Components/common/Touchable';
import { RootStackParamList } from '../../navigator';
import PillBase from './PillBase';

type Nav = NativeStackNavigationProp<RootStackParamList>;

const PLACEHOLDER_PILLS = 5;
const MAX_PILLS = 5;

// Figma "Pill / Daily Missions" (907:323) sets the title in Label/Tiny - Sofia Sans SemiBold
// 10/12 - on the 126-wide Mobile pill. The Desktop variant is 216 wide but only grows it to
// 16/17.6, so a desktop layer that draws this pill scaled up by 216 / 126 asks for the title at
// that ratio instead. Both variants hang the content from the top - 12 down once scaled - with
// the meter 7 under the title box, which is 10 high on Mobile.
const MOBILE_TITLE = { fontSize: 10, lineHeight: 12, height: 10 } as const;
const DESKTOP_TITLE_SCALE = 126 / 216;
const DESKTOP_TITLE = {
	fontSize: 16 * DESKTOP_TITLE_SCALE,
	lineHeight: 17.6 * DESKTOP_TITLE_SCALE,
	height: 17.6 * DESKTOP_TITLE_SCALE,
} as const;

type DailyMissionsCardProps = {
	/** The pill is being drawn for a desktop layer, scaled up: use the Desktop variant's title. */
	desktop?: boolean;
};

// The same 126x48 pill on every screen; the header and the desktop layers scale it with ScaledBox.
const DailyMissionsCard = ({ desktop = false }: DailyMissionsCardProps = {}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { navigate } = useNavigation<Nav>();
	const { isWeb } = useDevice();
	const styles = createStyles(desktop ? DESKTOP_TITLE : MOBILE_TITLE);

	const isLogged = useSelector(selectIsLogged);
	const total = useSelector(selectMissionsTotalCount);
	const completed = useSelector(selectMissionsCompletedCount);

	const pillCount = total > 0 ? Math.min(total, MAX_PILLS) : PLACEHOLDER_PILLS;
	const litPills = total > 0 ? Math.round((completed / total) * pillCount) : 0;

	const handlePress = () => {
		if (!isLogged) {
			navigate('Login');
			return;
		}

		dispatch(missionsModalOpened());
	};

	return (
		<Pressable onPress={handlePress} style={[styles.card, isWeb && styles.cardWeb]}>
			<PillBase />
			<View style={styles.iconWrap}>
				<Image source={DailyMissionsTargetPng} resizeMode='contain' style={styles.icon} />
			</View>

			<View style={styles.content}>
				<Text numberOfLines={1} style={styles.title}>
					{total > 0
						? t('T_SITE_HOME_DAILY_MISSIONS_COUNT', {
								defaultValue: 'Daily missions {{completed}}/{{total}}',
								completed,
								total,
							})
						: t('T_SITE_HOME_DAILY_MISSIONS', { defaultValue: 'Daily missions' })}
				</Text>
				<View style={styles.progressRow}>
					{Array.from({ length: pillCount }, (_, index) => (
						<View
							key={index}
							style={[styles.progressPill, index < litPills && styles.progressPillActive]}
						/>
					))}
				</View>
			</View>
		</Pressable>
	);
};

const createStyles = (pillTitle: typeof MOBILE_TITLE | typeof DESKTOP_TITLE) =>
	StyleSheet.create({
		// The plate, border and glow are the design's own artwork, painted by PillBase.
		card: {
			width: 126,
			height: 48,
			paddingTop: 12,
			paddingHorizontal: 6,
			flexDirection: 'row',
			alignItems: 'flex-start',
		},
		cardWeb: {
			cursor: 'pointer',
		},
		iconWrap: {
			width: 28,
			height: 28,
			justifyContent: 'center',
			alignItems: 'center',
			marginRight: 2,
		},
		icon: {
			width: 28,
			height: 28,
		},
		content: {
			flexShrink: 1,
			gap: 7,
		},
		title: {
			color: '#FFFFFF',
			fontFamily: 'Sofia Sans',
			fontSize: pillTitle.fontSize,
			lineHeight: pillTitle.lineHeight,
			height: pillTitle.height,
			fontWeight: '600',
		},
		// Figma "Progress": five 14.4 x 6.5 segments, 2 apart, 79.8 across.
		progressRow: {
			width: 79.826,
			flexDirection: 'row',
			alignItems: 'center',
			gap: 2,
			height: 6.522,
		},
		progressPill: {
			flex: 1,
			height: 6.522,
			borderRadius: 20,
			backgroundColor: '#7C1B1B',
		},
		progressPillActive: {
			backgroundColor: '#FFC731',
		},
	});

export default DailyMissionsCard;
